import { nanoid } from "@reduxjs/toolkit";
import { api } from "../../app/api";
import { command } from "../../app/command";
import { query } from "../../app/query";
import { TempPasswordUpdated } from "../../common/components/functional/tempPasswordDialog/TempPasswordDialog";
import filterFalsyParameters from "../../common/helpers/filterFalsyParameters";
import { EventPayload } from "../../common/types/Event";
import { Paginated } from "../../common/types/Paginated";
import { Options } from "../../common/types/requestOptions";
import { InvoiceLanguage } from "../invoices/invoiceAPI";
import { TalentStatus } from "../talents/types/common";
import {
  AddWorkLocation,
  CreateCustomerCompany,
  CreateCustomerContract,
  CreateCustomerCostCenter,
  CreateCustomerOrganization,
  CreateCustomerUser,
  InviteExistingCustomerUser,
  InviteNewCustomerUser,
  UpdateActiveStatus,
  UpdateCustomerCompanyClassification,
  UpdateCustomerCostCenter,
  UpdateCustomerOrganization,
  UpdateCustomerUser,
  UpdateCustomerUserEnabledState,
  UpdateOrderingUserWorkLocations,
  UpdateWorkLocation,
} from "./types/commands";
import {
  CustomerCompanyClassificationChanged,
  CustomerCompanyCreated,
  CustomerContractCreated,
  CustomerCostCenterChanged,
  CustomerCostCenterCreated,
  CustomerOrganizationChanged,
  CustomerOrganizationCreated,
  CustomerUserCreated,
  ExistingCustomerUserInvited,
  NewCustomerUserInvited,
  OrderingUserWorkLocationsChanged,
  WorkLocationAdded,
  WorkLocationUpdated,
} from "./types/events";
import {
  CityAndPostOfficeByZip,
  CityAndPostOfficeByZipResponse,
  CustomerCompanyIdFilter,
  CustomerCompanyWithOrganizations,
  CustomerCostCenterDetails,
  CustomerCostCenterDetailsFilter,
  CustomerCostCentersByOrgIdsFilters,
  CustomerCostCentersResult,
  CustomerOrganizationDetails,
  CustomerOrganizations,
  CustomerOrganizationsAutocompleteResult,
  CustomerOrganizationsPageFilters,
  CustomerOrganizationsResult,
  CustomerUsersAutocompleteFilters,
  CustomerUsersAutocompleteResponse,
  CustomerWorkLocationsAndContracts,
  CustomerWorkLocationsAndOrganizations,
  WorkLocationById,
  WorkLocation as WorkLocationExtendend,
  WorkTitlesAutoCompleteFilters,
  WorkTitlesAutoCompleteResponse,
  CustomerOrganization as CustomerOrgByIdResult,
  SalesPermission,
} from "./types/queries";

// TODO: rename to WorkLocationOption etc
export interface WorkLocation {
  id: string;
  name: string;
}

export interface Contract {
  name: string;
  id: string;
}

export interface CustomerOrganization {
  name: string;
  id: string;
  salesPermission?: SalesPermission;
}

export interface CustomerOrganizationWithCostCenters
  extends CustomerOrganization {
  costCenters?: CustomerCostCenter[];
}

export interface ContractCustomerPerson {
  id: string;
}

export interface Factoring {
  factoringContractId: string;
  salesAccount: string;
  customerOrgIds: string[];
}

export interface CustomerContract {
  id: string;
  customerCompanyId: string;
  eezyCompanyId: string;
  name: string;
  validFrom?: string;
  version: number;
  pricing?: any; // TODO
  salesAccount?: {
    code?: string;
    canUseInContract?: boolean;
    canUseInGroupInternalSales?: boolean;
    defaultVatPercent?: string;
    description?: string;
    defaultVatCode?: string;
    canUseInExternalSales?: boolean;
  };
  schedulingClassId?: string;
  monthlyWorkSchedulingClassId?: string;
  invoiceFormat?: string;
  invoiceBreakdown?: string[];
  factoring?: Factoring;
  invoiceReferences?: {
    agreementIdentifier?: string;
    orderIdentifier?: string;
    sellerReferenceIdentifier?: string;
    buyerReferenceIdentifier?: string;
  };
  paymentTermDays?: number;
  salesPersonId: string;
  salesPersonName?: string;
  contractResponsiblePerson?: ContractCustomerPerson;
  invoicingResponsiblePerson?: ContractCustomerPerson;
  invoicesByCostCenter?: boolean;
  invoicesByWorkLocation?: boolean;
  invoiceDeliveryDate: boolean;
  invoiceLanguage: InvoiceLanguage;
  eezyCompany: { id: string; name: string; businessId: string };
  customerOrgId?: string[];
  orderQueueId?: string;
}

export interface CustomerCompany {
  id: string;
  name: string;
  version: number;
  businessId: string;
  contracts: CustomerContract[];
}

export interface CustomerCompanyResult {
  businessId: string;
  customerCompanyId: string;
  customerGroupId?: string;
  name: string;
  type: string;
  version: number;
}

export interface CustomerCostCenter {
  id: string;
  name: string;
  code: string;
  version: number;
  active: boolean;
}

export type WorkLocationAndCustomerOrgIdFilter = {
  workLocationId: string;
  customerOrgId: string;
};
export type IdFilter = {
  id: string;
};
export type NameFilter = {
  name: string;
};

export interface CustomerOrgAndCompAutoCompleteFilters extends NameFilter {}

export type TermFilter = {
  term: string;
};

export interface CustomerOrgAutoCompleteFilters extends TermFilter {}

export interface CustomerCompanyFilters {
  id: string;
}

export interface WorkLocationFilters {
  term: string;
  customerCompanyId?: string;
}

export interface ContractEvent {
  id: string;
  name: string;
  version: number;
  payload: any;
}

export interface ContractPricingEvent {
  id: string;
  name: string;
  version: number;
  payload: any;
}

export interface ContractUpdatePost {
  id: string;
  version: number;
  paymentTermDays: number;
  invoiceFormat: string;
  salesPersonId: string;
  salesPersonName: string;
  invoicesByCostCenter: boolean;
  invoicesByWorkLocation: boolean;
  invoiceDeliveryDate: boolean;
  invoiceLanguage: string;
}

export interface ContractPricingUpdatePost {
  id: string;
  version: number;
  validFrom: string;
  pricing: any;
  backDate?: string;
}

export interface TesAutocompleteFilters {
  term: string;
  languageCode: string;
}

export interface Tes {
  id: string;
  name: string;
}

export interface customerOrgNamesFilters {
  ids: string;
}

export interface customerOrgNamesResult {
  id: string;
  name: string;
}

export interface TesExpenseTypeCode {
  code: string;
  category: string;
  pricingCategory?: string;
}

export interface TesExpenseTypeCodesQueryFilters {
  tesId: string;
  pricingCategory?: string;
}

export interface WorkLocationIdFilter {
  workLocationId: string;
}

export interface CustomerOrgIdFilter {
  customerOrgId: string;
}

export interface WorkLocationPoolFilter extends WorkLocationIdFilter {
  includeNotActiveTalents?: boolean;
}

export interface WorkLocationsIdsFilter {
  workLocationIds: string[];
}

export type WorkLocationsByCustomerCompanyIdFilters = {
  customerCompanyId: string;
};

export type CustomerCostCentersFilter = {
  search: string;
  customerOrgId?: string;
};

export interface SetCustomerUserTemporaryPassword {
  customerUserId: string;
}

export interface PoolTalent {
  id: string;
  firstName: string;
  lastName: string;
  status: TalentStatus;
}

export interface WorkLocationTalentPool {
  workLocationId: string;
  talents: PoolTalent[];
}

export interface AddOrRemovePoolTalents {
  workLocationId: string;
  talentIds: string[];
}

export interface AutocompleteCustomerCC {
  id: string;
  name: string;
  customerOrgId: string;
  customerOrgName: string;
}

export const customerOrgNames = async (
  filters: customerOrgNamesFilters,
  { requestId }: Options,
): Promise<customerOrgNamesResult[]> => {
  return api
    .get<Promise<customerOrgNamesResult[]>>(
      "/projections/qry/customerOrgNames",
      {
        requestId,
        auth: true,
        queryParams: {
          ...filters,
        },
      },
    )
    .then((res) => res.data);
};

export const getCustomerOrganizationsById = async (
  filters: CustomerCompanyIdFilter,
  { requestId }: Options,
): Promise<CustomerOrganization[]> => {
  return api
    .get<CustomerOrgByIdResult[]>(
      "/projections/qry/customerOrganizationsById",
      {
        requestId,
        auth: true,
        queryParams: {
          customerCompanyId: filters.customerCompanyId,
        },
      },
    )
    .then((res) =>
      res.data.map((org) => ({
        id: org.customerOrgId,
        name: org.name,
        salesPermission: org.salesPermission,
      })),
    );
};

export const getCustomerOrganizations = async (
  filters: CustomerOrganizations,
  { requestId }: Options,
): Promise<CustomerOrganizationsResult[]> => {
  return api
    .get<CustomerOrganizationsResult[]>(
      "/projections/qry/customerOrganizations",
      {
        requestId,
        auth: true,
        queryParams: { ...filterFalsyParameters(filters) },
      },
    )
    .then((res) => res.data);
};

export const getCustomerOrganizationsPaged = async (
  filters: CustomerOrganizationsPageFilters,
  { requestId }: Options = {},
): Promise<Paginated<CustomerOrganizationsResult[]>> => {
  return api
    .get<Paginated<CustomerOrganizationsResult[]>>(
      "/projections/qry/customerOrganizationsPaged",
      {
        requestId,
        auth: true,
        queryParams: { ...filterFalsyParameters(filters) },
      },
    )
    .then((res) => res.data);
};

/**
 * @deprecated Use getCustomerOrganizations instead
 */
export const getCustomerCompanies = async ({
  requestId,
}: Options): Promise<CustomerCompanyWithOrganizations[]> => {
  return api
    .get<CustomerCompanyWithOrganizations[]>(
      "/projections/qry/customerCompanies",
      {
        requestId,
        auth: true,
      },
    )
    .then((res) => res.data);
};

export const getCustomerCostCentersByCompanyId = async (
  filters: CustomerCompanyIdFilter,
  { requestId }: Options,
): Promise<CustomerCostCentersResult[]> => {
  return api
    .get<Promise<CustomerCostCentersResult[]>>(
      "/projections/qry/customerCostCentersByCompanyId",
      {
        requestId,
        auth: true,
        queryParams: {
          customerCompanyId: filters.customerCompanyId,
        },
      },
    )
    .then((res) => res.data);
};

export const getCustomerCostCentersByWorkLocationId = (
  filters: WorkLocationIdFilter,
  options: Options = {},
): Promise<CustomerCostCentersResult[]> =>
  query<WorkLocationIdFilter, CustomerCostCentersResult[]>(
    "/projections/qry/customerCostCentersByWorkLocationId",
    filters,
    options,
  );

export const listCustomerOrganizations = ({ requestId }: Options = {}) => {
  return api
    .get<CustomerOrganization[]>("/invoicing/qry/customerorganizations", {
      requestId,
      auth: true,
      queryParams: {},
    })
    .then((res) => {
      let orgs: CustomerOrganization[] = [];
      for (let i = 0; i < res.data.length; i++) {
        let org = res.data[i] as CustomerOrganization;
        orgs.push(org);
      }
      return orgs;
    });
};
export const customerOrgAutocompleteQuery = async (
  filters: CustomerOrgAutoCompleteFilters,
  { requestId }: Options = {},
) => {
  const res = await api.get<Array<CustomerOrganizationsAutocompleteResult>>(
    "/projections/qry/customerOrgAutocomplete",
    {
      requestId,
      auth: true,
      queryParams: {
        search: filters.term,
      },
    },
  );
  return res.data;
};

export const getWorkLocationWithOrgById = (
  filters: WorkLocationAndCustomerOrgIdFilter,
  { requestId }: Options = {},
): Promise<CustomerWorkLocationsAndOrganizations> => {
  return api
    .get<CustomerWorkLocationsAndOrganizations>(
      "/projections/qry/workLocationWithCustOrgById",
      {
        requestId,
        auth: true,
        queryParams: {
          workLocationId: filters.workLocationId,
          customerOrgId: filters.customerOrgId,
        },
      },
    )
    .then((res) => {
      return res.data;
    });
};

export const listWorkLocationsAndContractsByOrgId = (
  filters: IdFilter,
  { requestId }: Options = {},
): Promise<CustomerWorkLocationsAndContracts> => {
  return api
    .get<CustomerWorkLocationsAndContracts>(
      "/projections/qry/workLocationsAndContractsByCustomerOrgId",
      {
        requestId,
        auth: true,
        queryParams: {
          customerOrgId: filters.id,
        },
      },
    )
    .then((res) => {
      return res.data;
    });
};

export const customerOrgById = (
  filters: IdFilter,
  { requestId }: Options = {},
) => {
  return api
    .get<Promise<CustomerOrganizationDetails>>(
      "/projections/qry/customerOrgById",
      {
        requestId,
        auth: true,
        queryParams: {
          customerOrgId: filters.id,
        },
      },
    )
    .then((res) => {
      return res.data;
    });
};

export const customerOrgAutocomplete = (filters: NameFilter) => {
  return api
    .get<any>("/projections/qry/customerOrgAutocomplete", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        search: filters.name,
      },
    })
    .then((res) => res.data);
};

export const customerOrgAndCompAutocomplete = (
  filters: CustomerOrgAndCompAutoCompleteFilters,
) => {
  return api
    .get<any>("/projections/qry/customerOrgAndCompAutocomplete", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        search: filters.name,
      },
    })
    .then((res) => res.data);
};

export const getCustomerCompanyById = (
  filters: CustomerCompanyFilters,
  { requestId }: Options = {},
) => {
  return api
    .get<CustomerCompany>("/projections/qry/customerCompanyById", {
      requestId,
      auth: true,
      queryParams: {
        customerCompanyId: filters.id,
      },
    })
    .then((res) => {
      return res.data;
    });
};

export const getCustomerOrgContracts = (
  customerOrgId: string,
  { requestId }: Options = {},
) => {
  return api.get<CustomerContract[]>(
    "/projections/qry/contractsByCustomerOrgId",
    {
      requestId,
      auth: true,
      queryParams: {
        customerOrgId,
      },
    },
  );
};

export const getContractById = (
  filters: { contractId: string },
  options: Options = {
    requestId: nanoid(),
  },
): Promise<CustomerContract> =>
  query<{ contractId: string }, CustomerContract>(
    "/projections/qry/contractById",
    { contractId: filters.contractId },
    options,
  );

export const workLocationsAutoCompleteQuery = (filters: WorkLocationFilters) =>
  api
    .get<WorkLocationExtendend[]>("/projections/qry/workLocationAutocomplete", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        customerCompanyId: filters.customerCompanyId || "",
        search: filters.term,
      },
    })
    .then((res) => res.data);

export const updateCustomerContract = (
  contract: ContractUpdatePost,
  { requestId }: Options,
) =>
  api.post<ContractEvent[]>("/invoicing/cmd/updateContract", {
    requestId,
    auth: true,
    body: {
      ...contract,
    },
  });

export const updateCustomerContractPricing = (
  pricing: ContractPricingUpdatePost,
  { requestId }: Options,
) =>
  api.post<any>("/invoicing/cmd/updateContractPricing", {
    requestId,
    auth: true,
    body: {
      ...pricing,
    },
  });

export const updateWorkLocation = (
  worklocation: UpdateWorkLocation,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<WorkLocationUpdated>[]>(
      "/invoicing/cmd/updateWorkLocation",
      {
        requestId,
        auth: true,
        body: {
          ...worklocation,
        },
      },
    )
    .then((res) => res.data);

export const addWorkLocation = (
  worklocation: AddWorkLocation,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<WorkLocationAdded>[]>("/invoicing/cmd/addWorkLocation", {
      requestId,
      auth: true,
      body: {
        ...worklocation,
      },
    })
    .then((res) => res.data[0]);

export const updateCustomerOrganization = (
  organization: UpdateCustomerOrganization,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<CustomerOrganizationChanged>[]>(
      "/invoicing/cmd/updateCustomerOrganization2",
      {
        requestId,
        auth: true,
        body: {
          ...organization,
        },
      },
    )
    .then((res) => res.data);

export const customerCostCenterById = (
  filters: CustomerCostCenterDetailsFilter,
  { requestId }: Options,
): Promise<CustomerCostCenterDetails> =>
  api
    .get<CustomerCostCenterDetails>("/projections/qry/customerCostCenterById", {
      requestId,
      auth: true,
      queryParams: {
        customerCostCenterId: filters.customerCostCenterId,
      },
    })
    .then((res) => res.data);

export const customerCostCentersByOrgIds = (
  filters: CustomerCostCentersByOrgIdsFilters,
  { requestId }: Options,
): Promise<CustomerCostCenterDetails[]> =>
  api
    .get<CustomerCostCenterDetails[]>(
      "/projections/qry/customerCostCentersByOrgIds",
      {
        requestId,
        auth: true,
        queryParams: {
          customerCCIds: filters.customerCCIds,
          customerOrgIds: filters.customerOrgIds,
        },
      },
    )
    .then((res) => res.data);

export const createCustomerCostCenter = (
  costCenter: CreateCustomerCostCenter,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<CustomerCostCenterCreated>[]>(
      "/invoicing/cmd/createCustomerCostCenter",
      {
        requestId,
        auth: true,
        body: {
          ...costCenter,
        },
      },
    )
    .then((res) => res.data);

export const updateCustomerCostCenter = (
  costCenter: UpdateCustomerCostCenter,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<CustomerCostCenterChanged>[]>(
      "/invoicing/cmd/updateCustomerCostCenter",
      {
        requestId,
        auth: true,
        body: {
          ...costCenter,
        },
      },
    )
    .then((res) => res.data);

export const tesAutocompleteQuery = (filters: TesAutocompleteFilters) =>
  api
    .get<Tes[]>("/projections/qry/tesAutocomplete", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        search: filters.term,
        language: filters.languageCode,
      },
    })
    .then((res) => res.data);

export const updateCustomerWorkLocationActiveStatus = (
  params: UpdateActiveStatus,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<{}>[]>("/invoicing/cmd/updateWorkLocationActiveStatus", {
      requestId,
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data[0]);

export const updateCustomerCostCenterActiveStatus = (
  params: UpdateActiveStatus,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<{}>[]>(
      "/invoicing/cmd/updateCustomerCostCenterActiveStatus",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data[0]);

export const createCustomerUser = (
  params: CreateCustomerUser,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<CustomerUserCreated>[]>(
      "/identity/cmd/createCustomerUser",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data);

export const inviteExistingCustomerUser = (
  customerUser: InviteExistingCustomerUser,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<ExistingCustomerUserInvited>[]>(
      "/identity/cmd/inviteExistingCustomerUser",
      {
        requestId,
        auth: true,
        body: {
          id: customerUser.id,
        },
      },
    )
    .then((res) => res.data);

export const inviteNewCustomerUser = (
  customerUser: InviteNewCustomerUser,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<NewCustomerUserInvited>[]>(
      "/identity/cmd/inviteNewCustomerUser",
      {
        requestId,
        auth: true,
        body: {
          ...customerUser,
        },
      },
    )
    .then((res) => res.data);

export const updateOrderingUserWorkLocations = (
  params: UpdateOrderingUserWorkLocations,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<OrderingUserWorkLocationsChanged>[]>(
      "/invoicing/cmd/updateOrderingUserWorkLocations",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data);

export const updateCustomerUser = (
  params: UpdateCustomerUser,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<UpdateCustomerUser>[]>(
      "/identity/cmd/updateCustomerUser",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data[0]);

export const updateCustomerUserEnabledState = (
  params: UpdateCustomerUserEnabledState,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<UpdateCustomerUserEnabledState>[]>(
      "/identity/cmd/updateCustomerUserEnabledState",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data[0]);

export const cityAndPostOfficeByZip = (
  filters: CityAndPostOfficeByZip,
  { requestId }: Options,
): Promise<CityAndPostOfficeByZipResponse> =>
  api
    .get<CityAndPostOfficeByZipResponse>(
      "/projections/qry/cityAndPostOfficeByZip",
      {
        requestId,
        auth: true,
        queryParams: {
          ...filters,
        },
      },
    )
    .then((res) => res.data);

export const workTitlesAutoCompleteQuery = (
  filters: WorkTitlesAutoCompleteFilters,
  { requestId }: Options,
): Promise<WorkTitlesAutoCompleteResponse[]> =>
  api
    .get<WorkTitlesAutoCompleteResponse[]>(
      "/projections/qry/workTitlesAutoComplete",
      {
        requestId,
        auth: true,
        queryParams: {
          ...filters,
        },
      },
    )
    .then((res) => res.data);

export const tesExpenseTypeCodesQuery = (
  filters: TesExpenseTypeCodesQueryFilters,
  { requestId }: Options,
): Promise<TesExpenseTypeCode[]> =>
  api
    .get<TesExpenseTypeCode[]>("/projections/qry/tesExpenseTypeCodes", {
      requestId,
      auth: true,
      queryParams: {
        ...filters,
      },
    })
    .then((res) => res.data);

export const workLocationById = (
  filters: WorkLocationIdFilter,
  { requestId }: Options,
): Promise<WorkLocationById> =>
  api
    .get<WorkLocationById>("/projections/qry/workLocationById", {
      requestId,
      auth: true,
      queryParams: {
        ...filters,
      },
    })
    .then((res) => res.data);

export const workLocationsByIds = async (
  filters: WorkLocationsIdsFilter,
  { requestId }: Options,
): Promise<WorkLocationById[]> => {
  if (!filters.workLocationIds.length) {
    return [];
  }

  return api
    .get<WorkLocationById[]>("/projections/qry/workLocationsByIds", {
      requestId,
      auth: true,
      queryParams: {
        workLocationIds: filters.workLocationIds.join(","),
      },
    })
    .then((res) => res.data);
};

export const workLocationsByCustomerCompanyId = (
  filters: WorkLocationsByCustomerCompanyIdFilters,
): Promise<WorkLocationExtendend[]> =>
  api
    .get<WorkLocationExtendend[]>(
      "/projections/qry/customerWorkLocationsByCustomerCompanyId",
      {
        requestId: nanoid(),
        auth: true,
        queryParams: {
          ...filters,
        },
      },
    )
    .then((res) => res.data);

export const runSetCustomerUserTemporaryPassword = (customerUserId: string) =>
  api.post<TempPasswordUpdated>(
    "/identity/cmd/setCustomerUserTemporaryPassword",
    {
      requestId: nanoid(),
      auth: true,
      body: {
        customerUserId,
      },
    },
  );

export const customerUsersAutoCompleteQuery = (
  filters: CustomerUsersAutocompleteFilters,
  { requestId }: Options,
): Promise<CustomerUsersAutocompleteResponse[]> =>
  api
    .get<CustomerUsersAutocompleteResponse[]>(
      "/projections/qry/customerUsersAutocomplete",
      {
        requestId,
        auth: true,
        queryParams: {
          ...filters,
        },
      },
    )
    .then((res) => res.data);

export const runCreateCustomerCompany = (
  body: CreateCustomerCompany,
  options: Options = {},
) =>
  command<
    CreateCustomerCompany,
    CustomerCompanyCreated & CustomerOrganizationCreated
  >("/invoicing/cmd/createCustomer", body, options);

export const runCreateCustomerOrganization = (
  body: CreateCustomerOrganization,
  options: Options = {},
) =>
  command<CreateCustomerOrganization, CustomerOrganizationCreated>(
    "/invoicing/cmd/createCustomerOrganization",
    body,
    options,
  );

export const runCreateCustomerContract = (
  body: CreateCustomerContract,
  options: Options = {},
) =>
  command<CreateCustomerContract, CustomerContractCreated>(
    "/invoicing/cmd/createContract",
    body,
    options,
  );

export const runUpdateCustomerCompanyClassification = (
  body: UpdateCustomerCompanyClassification,
  options: Options = {},
) =>
  command<
    UpdateCustomerCompanyClassification,
    CustomerCompanyClassificationChanged
  >("/invoicing/cmd/updateCustomerClassification", body, options);

export const getWorkLocationTalentPool = (
  filters: WorkLocationIdFilter,
  options: Options = {},
): Promise<WorkLocationTalentPool> =>
  query<WorkLocationPoolFilter, WorkLocationTalentPool>(
    "/projections/qry/workLocationTalentPoolById",
    filters,
    options,
  );

export const runRemoveTalentsFromPool = (
  body: AddOrRemovePoolTalents,
  options: Options = {},
) =>
  command<AddOrRemovePoolTalents, EventPayload<{}>>(
    "/matching/cmd/removeTalentsFromWorkLocationTalentPool",
    body,
    options,
  );

export const runAddTalentsToPool = (
  body: AddOrRemovePoolTalents,
  options: Options = {},
) =>
  command<AddOrRemovePoolTalents, EventPayload<{}>>(
    "/matching/cmd/addTalentsToWorkLocationTalentPool",
    body,
    options,
  );

export const customerCompanyByOrgId = (
  filters: CustomerOrgIdFilter,
  options: Options = {},
): Promise<CustomerCompanyResult> =>
  query<CustomerOrgIdFilter, CustomerCompanyResult>(
    "/projections/qry/customerCompanyByOrgId",
    filters,
    options,
  );

export const customerCostCenterAutocomplete = (
  filters: CustomerCostCentersFilter,
  options: Options = {
    requestId: nanoid(),
  },
): Promise<AutocompleteCustomerCC[]> =>
  query<CustomerCostCentersFilter, AutocompleteCustomerCC[]>(
    "/projections/qry/customerCostCenterAutocomplete",
    filters,
    options,
  );
